import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { apiPost } from '../../api/apiClient';
import logo from '../login/logo.png'; // Adjust the path as necessary
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { toastSuccess, toastError } from '../common/toast';
import { useNavigate } from 'react-router-dom';
import './LoginForm.css';
import tenantStorage from '../../utils/tenantStorage';

const ChangePasswordForm = ({ mode = 'CHANGE_PASSWORD', username = '', session }) => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [success, setSuccess] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [currentUsername, setCurrentUsername] = useState(username);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (mode === 'NEW_PASSWORD_REQUIRED') {
            setCurrentUsername(username);
        }
        // For CHANGE_PASSWORD mode, username is now extracted from JWT token on backend
    }, [mode, username]);

    // Check if user is logged in when accessing change password directly
    useEffect(() => {
        if (mode === 'CHANGE_PASSWORD') {
            const accessToken = tenantStorage.getItem('accessToken');
            if (!accessToken) {
                // User not logged in, redirect to login
                navigate('/login', { state: { mode: 'SESSION_EXPIRED' } });
            }
        }
    }, [mode, navigate]);

    const validatePassword = (password) => {
        const errors = [];

        // Check minimum 6 characters
        if (password.length < 6) {
            errors.push('Password must be at least 6 characters long');
        }

        // Check for alphabets
        if (!/[a-zA-Z]/.test(password)) {
            errors.push('Password must contain at least one alphabet');
        }

        // Check for at least one number
        if (!/[0-9]/.test(password)) {
            errors.push('Password must contain at least one number');
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccess('');

        // Check if old password is required and entered
        if (mode === 'CHANGE_PASSWORD' && !oldPassword.trim()) {
            toastError('Current password is required', 3000);
            return;
        }

        // For NEW_PASSWORD_REQUIRED mode, validate username is provided
        if (mode === 'NEW_PASSWORD_REQUIRED' && !currentUsername.trim()) {
            toastError('Username is required', 3000);
            return;
        }

        if (newPassword !== confirmPassword) {
            toastError('New password and confirm password do not match', 3000);
            return;
        }

        // Validate password rules
        const validationErrors = validatePassword(newPassword);
        if (validationErrors.length > 0) {
            toastError(validationErrors.join('. '), 4000);
            return;
        }

        setIsLoading(true);
        changePassword(mode === 'NEW_PASSWORD_REQUIRED' ? currentUsername : null, newPassword, session, oldPassword).then((response) => {
            if (response.message) {
                // Show success toast
                toastSuccess('Password changed successfully!');
                if (mode === 'CHANGE_PASSWORD') {
                    tenantStorage.clear();
                }
                setIsLoading(false);
                navigate('/login', { state: { mode: 'PASSWORD_CHANGED' } });
            }
        }).catch((err) => {
            setIsLoading(false);
            
            // Extract error message from API response
            let errorMessage = 'Failed to change password. Please try again.';
            let shouldRedirectToLogin = false;
            
            if (err.response && err.response.data && err.response.data.detail) {
                const detail = err.response.data.detail;
                
                // Handle specific AWS Cognito errors with user-friendly messages
                if (detail.includes('LimitExceededException') || detail.includes('Attempt limit exceeded')) {
                    errorMessage = 'Too many password change attempts. Please wait a few minutes before trying again.';
                } else if (detail.includes('Current password is incorrect')) {
                    errorMessage = 'Current password is incorrect. Please check and try again.';
                } else if (detail.includes('InvalidPasswordException') || detail.includes('Password does not meet requirements')) {
                    errorMessage = 'New password does not meet security requirements. Please choose a stronger password.';
                } else if (detail.includes('NotAuthorizedException') || detail.includes('InvalidParameterException')) {
                    errorMessage = 'Authentication failed. Please log in again.';
                    shouldRedirectToLogin = true;
                } else {
                    errorMessage = detail;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            // Show error in toast with auto-close
            toastError(errorMessage, 5000); // Show for 5 seconds for error messages
            
            // For NEW_PASSWORD_REQUIRED flow, redirect to login on authentication errors
            if (mode === 'NEW_PASSWORD_REQUIRED' && shouldRedirectToLogin) {
                setTimeout(() => {
                    navigate('/login', { state: { mode: 'PASSWORD_CHANGE_ERROR' } });
                }, 2000); // Wait 2 seconds for user to see the error message
            }
        });
    }

    // Function to handle password change
    const changePassword = async (username, newPassword, session, oldPassword) => {
        const requestBody = { new_password: newPassword };
        
        // Only include username if provided (for NEW_PASSWORD_REQUIRED mode)
        if (username) {
            requestBody.username = username;
        }
        
        // Include old password for logged-in users (CHANGE_PASSWORD mode)
        if (oldPassword && mode === 'CHANGE_PASSWORD') {
            requestBody.old_password = oldPassword;
        }
        
        const headers = {
            'Content-Type': 'application/json'
        };
        
        // Add session header if provided
        if (session) {
            headers.session = session;
        }
        
        // For logged-in users (CHANGE_PASSWORD mode), the Authorization header 
        // will be automatically added by the API client
        
        const response = await apiPost('/changepassword', requestBody, { headers });
        return response.data;
    };

    return (
        <>
            <div className="login-container">
                <div>
                    <img
                        src={logo}
                        alt="App Logo"
                        className="login-logo"
                    />
                </div>
                <div className="change-password-form">
                <h3 className="rt-title" style={{ textAlign: 'center', marginBottom: '1.5rem', fontSize: '1.25rem' }}>Change Password</h3>
                {mode === 'NEW_PASSWORD_REQUIRED' && (
                    <div className="alert alert-info text-center">
                        You need to change your password before you can login
                    </div>
                )}
                {success && <div className="alert alert-success">{success}</div>}
                <Form onSubmit={handleSubmit}>
                    {mode === 'NEW_PASSWORD_REQUIRED' && (
                        <Form.Group controlId="formUsername" className="form-group">
                            <Form.Label>Username:</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter username"
                                value={currentUsername}
                                onChange={(e) => setCurrentUsername(e.target.value)}
                                required
                            />
                        </Form.Group>
                    )}
                    {mode === 'CHANGE_PASSWORD' && (
                        <Form.Group controlId="formOldPassword" className="form-group">
                            <Form.Label>Current Password:</Form.Label>
                            <div className="password-input-container">
                                <Form.Control
                                    type={showOldPassword ? "text" : "password"}
                                    placeholder={mode === 'CHANGE_PASSWORD' ? 'Enter current password' : 'Enter old password'}
                                    value={oldPassword}
                                    onChange={(e) => setOldPassword(e.target.value)}
                                />
                                <RTButton
                                    type="button"
                                    iconMode={true}
                                    className="password-toggle-btn"
                                    onClick={() => setShowOldPassword(!showOldPassword)}
                                >
                                    {showOldPassword ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </RTButton>
                            </div>
                        </Form.Group>
                    )}
                    <Form.Group controlId="formNewPassword" className="form-group">
                        <Form.Label>New Password:</Form.Label>
                        <div className="password-input-container">
                            <Form.Control
                                type={showNewPassword ? "text" : "password"}
                                placeholder="Enter new password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                            <RTButton
                                type="button"
                                iconMode={true}
                                className="password-toggle-btn"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                                {showNewPassword ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </RTButton>
                        </div>
                        <Form.Text className="text-muted">
                            Password must be at least 6 characters long, contain alphabets and at least one number.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group controlId="formConfirmPassword" className="form-group">
                        <Form.Label>Confirm New Password:</Form.Label>
                        <div className="password-input-container">
                            <Form.Control
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                            <RTButton
                                type="button"
                                iconMode={true}
                                className="password-toggle-btn"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </RTButton>
                        </div>
                    </Form.Group>
                    <div className="form-buttons">
                        {mode !== 'NEW_PASSWORD_REQUIRED' && (
                            <RTButton 
                                type="button" 
                                variant="secondary" 
                                disabled={isLoading}
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </RTButton>
                        )}
                        <RTButton 
                            type="submit" 
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <RTSpinner size="small" />
                                    &nbsp;Changing...
                                </>
                            ) : (
                                'Change Password'
                            )}
                        </RTButton>
                    </div>
                </Form>
            </div>
        </div>
        {isLoading && <RTSpinner fullScreen={true} />}
        </>
    );
};

export default ChangePasswordForm;